import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { sellRelic } from 'store/relicVault';

// Timings from the designer's handoff (hooks/useVaultBrowser.ts).
const COIN_FLY_MS = 650; // coin flies up out of the sell popup
const BALANCE_BEAT_MS = 600; // old balance stays visible before it updates
const COIN_GLOW_MS = 900;

export const DEFAULT_GUILD_ID = 'quik';

/**
 * Page-local state for the vault browser: selected guild and relic, search,
 * owned/missing filter, and the two-stage sell animation.
 */
export function useVaultBrowser({ userId, onServerBalanceChanged }) {
  const dispatch = useDispatch();
  const [selectedGuildId, setSelectedGuildId] = useState(DEFAULT_GUILD_ID);
  const [selectedRelicId, setSelectedRelicId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [ownedFilter, setOwnedFilter] = useState('all');
  const [sellConfirmId, setSellConfirmId] = useState(null);
  const [sellAnimating, setSellAnimating] = useState(false);
  const [coinGlow, setCoinGlow] = useState(false);
  const timers = useRef([]);

  const later = (fn, ms) => {
    const t = setTimeout(fn, ms);
    timers.current.push(t);
  };

  useEffect(
    () => () => {
      timers.current.forEach(t => clearTimeout(t));
    },
    [],
  );

  const selectGuild = useCallback(id => {
    setSelectedGuildId(id);
    setSelectedRelicId(null);
    setSearchQuery('');
  }, []);

  const selectRelic = useCallback(id => setSelectedRelicId(id), []);

  const closeDetail = useCallback(() => {
    setSelectedRelicId(null);
    setSellConfirmId(null);
  }, []);

  const startSell = useCallback(id => setSellConfirmId(id), []);
  const cancelSell = useCallback(() => setSellConfirmId(null), []);

  const confirmSell = useCallback(
    id => {
      setSellAnimating(true);
      later(() => {
        // Close the popup first, balance untouched, so the current QuikCoins
        // amount is visible for a beat before it updates.
        setSellAnimating(false);
        setSellConfirmId(null);
        setSelectedRelicId(null);
        later(async () => {
          const result = await dispatch(sellRelic({ userId, relicId: id }));
          if (!result?.success) return;
          if (result.serverBalanceChanged && onServerBalanceChanged) {
            onServerBalanceChanged();
          }
          setCoinGlow(true);
          later(() => setCoinGlow(false), COIN_GLOW_MS);
        }, BALANCE_BEAT_MS);
      }, COIN_FLY_MS);
    },
    [dispatch, onServerBalanceChanged, userId],
  );

  return {
    selectedGuildId,
    selectedRelicId,
    searchQuery,
    setSearchQuery,
    ownedFilter,
    setOwnedFilter,
    sellConfirmId,
    sellAnimating,
    coinGlow,
    selectGuild,
    selectRelic,
    closeDetail,
    startSell,
    cancelSell,
    confirmSell,
  };
}
